// Classification + shaping of failures shown in action popups, so they can be
// attached to a pinned lead (see PopupFailureContext).

// Auth failures are handled globally (refresh / redirect) — never worth pinning.
export const AUTH_ERROR_PATTERNS = [
    'token is invalid or expired',
    'token is blacklisted',
    'given token not valid',
    'authentication credentials were not provided',
    'session has timed out',
    'refresh token not valid',
    'no active account found',
    'user inactive',
    // Refresh token validation — DRF returns {"refresh":["This field may not be null."]}
    // when localStorage refreshToken is null/expired. The apiCall.js refresh handler
    // already shows "Session has timed Out!" and redirects — this prevents the raw
    // JSON from also appearing as a second toast from the calling thunk.
    '"refresh"',
    'this field may not be null',
];

// Tag the pin thunks put on every updateError they dispatch (see leadPins slice).
export const PIN_ERROR_SOURCE = 'leadPins';

export const FAILURE_MESSAGE_MAX = 1000;

// Intentional request cancellations (AbortController / axios cancel) are not failures.
export const isCancellation = (error) => error?.name === 'CanceledError'
    || error?.name === 'AbortError'
    || error?.code === 'ERR_CANCELED';

// Messages errorHandler treats as expected user / validation hints (not faults).
export const EXPECTED_USER_MESSAGES = [
    'please select any one tab', // "Please select any one Tab" — 'any one' broke the old match
    'please select tab',         // keep as fallback for other tab-selection variants
    'not available for communication',
    'please add archive notes',
    'please select an archive reason',
    'unable to proceed',
    'please complete the required fields', // "Please complete the required fields before continuing."
    'scheduled time must be in the future', // "Scheduled time must be in the future"
];

// Shared with errorHandler: 401, a 403 that mentions permission / credentials,
// or a message matching a known auth pattern. `rawMessage` is the extracted
// message text (any case).
export function isAuthFailure(error, rawMessage) {
    const raw = typeof rawMessage === 'string' ? rawMessage : '';
    const lower = raw.toLowerCase();
    const isAuthStatus = error?.status === 401
        || (error?.status === 403 && (lower.includes('permission') || lower.includes('credentials')));
    return isAuthStatus || AUTH_ERROR_PATTERNS.some((p) => lower.includes(p));
}

export const isExpectedUserMessage = (rawMessage) => {
    const lower = String(rawMessage || '').toLowerCase();
    return EXPECTED_USER_MESSAGES.some((p) => lower.includes(p));
};

// Wrap a bare message (string / envelope object) as an updateError-style payload.
export const asErrorPayload = (value) => (
    value !== null && typeof value === 'object' && 'message' in value ? value : { message: value }
);

// The raw message text of an updateError payload, exactly as errorHandler reads
// it: `message` may be a string or an envelope object ({detail|message|error}).
// When error.message is already an object read the nested property directly —
// avoids a wasteful stringify→JSON.parse round-trip.
export function extractRawMessage(error) {
    const rawMsgSrc = error?.message;
    const rawErrValue = (rawMsgSrc !== null && typeof rawMsgSrc === 'object')
        ? (rawMsgSrc.detail || rawMsgSrc.message || rawMsgSrc.error || '')
        : (rawMsgSrc?.detail || rawMsgSrc || '');
    if (typeof rawErrValue === 'string') return rawErrValue;
    try { return JSON.stringify(rawErrValue) ?? ''; } catch { return ''; }
}

// The single source of truth for what the error toast SHOWS. errorHandler uses
// it for the snackbar and Sentry gating; popup failures use it so the banner,
// pin dialog and super-admin view show the same text the user saw.
export function classifyErrorMessage(error) {
    const rawErrorMsg = extractRawMessage(error);
    const isNotFound = error?.status === 404 || rawErrorMsg.includes('Not found.') || rawErrorMsg === '{"detail":"Not found."}';
    // Narrow the archived-lead heuristic: an FK-style phrase ("object does not
    // exist" / "invalid pk" — the generic DRF PrimaryKeyRelatedField failure) only
    // means "archived lead" when the thunk attached `error.lead` or the BE string
    // itself mentions "lead".
    const lowerRaw = rawErrorMsg.toLowerCase();
    const looksLikeFkError = lowerRaw.includes('object does not exist') || lowerRaw.includes('invalid pk');
    const mentionsLead = lowerRaw.includes('lead') || Boolean(error?.lead);
    const isArchivedLead = looksLikeFkError && mentionsLead;
    let errorMsg = rawErrorMsg;
    // Guard against JSON.stringify(null) → "null" from thunks using
    // `JSON.stringify(response?.data?.message) || 'Something went wrong'`
    if (errorMsg === 'null' || errorMsg === 'undefined' || errorMsg === '"null"') {
        errorMsg = 'Something went wrong';
    }
    // Normalise raw JSON-envelope messages to a friendly display string. Thunks
    // occasionally dispatch the raw API response object serialised as a string
    // (e.g. '{"success":false,"message":"An internal error occurred"}').
    if (errorMsg.startsWith('{') || errorMsg.startsWith('[')) {
        try {
            const parsed = JSON.parse(errorMsg);
            const extracted = parsed?.message || parsed?.detail || parsed?.error;
            errorMsg = (typeof extracted === 'string' && extracted && !extracted.startsWith('{'))
                ? extracted
                : 'Something went wrong';
        } catch {
            errorMsg = 'Something went wrong';
        }
    }
    // A bare HTML document (a server 404/500 page returned instead of JSON) must
    // never be shown raw to the user.
    const t = (errorMsg || '').trim().toLowerCase();
    const looksLikeHtmlBody = t.startsWith('<!doctype') || t.startsWith('<html') || t.includes('<title>not found</title>');
    if (isArchivedLead) errorMsg = 'This lead has been archived by another user.';
    else if (isNotFound || looksLikeHtmlBody) errorMsg = 'This record could not be found — it may have been deleted or moved.';
    return { rawErrorMsg, errorMsg, isNotFound, looksLikeHtmlBody, isArchivedLead };
}

// Exactly the string the toast shows for this payload.
export const toDisplayMessage = (error) => classifyErrorMessage(error).errorMsg;

// Display text of an updateError payload (or bare string), trimmed and capped.
export function errorToMessage(error) {
    if (error === null || error === undefined) return '';
    return String(toDisplayMessage(asErrorPayload(error)) || '').trim().slice(0, FAILURE_MESSAGE_MAX);
}

export function isPinnableFailure(errorOrMessage) {
    if (!errorOrMessage) return false;
    const error = asErrorPayload(errorOrMessage);
    // success toasts ('success' / true) and warnings are not failures.
    if (error.success || error.severity === 'warning') return false;
    // Toasts raised by the pin thunks themselves (see leadPins PIN_ERROR_SOURCE).
    if (error.source === PIN_ERROR_SOURCE) return false;
    if (isCancellation(error)) return false;
    const raw = extractRawMessage(error).trim();
    if (!raw || ['null', 'undefined', '"null"'].includes(raw)) return false;
    if (isAuthFailure(error, raw)) return false;
    return !isExpectedUserMessage(raw);
}

export function buildFailure({ message, popup, step, status }) {
    return {
        message: String(message || '').trim().slice(0, FAILURE_MESSAGE_MAX),
        context: {
            popup,
            step,
            status,
            route: window.location.pathname,
            at: new Date().toISOString(),
        },
    };
}
